import React, { useState, useEffect } from "react";
import StaticMapTile, { type MapOption } from "./StaticMapTile";
import { SPAWN_RATES, WORM_SPEEDS, POWERUPS, POWERUP_BADGE } from "../../shared/powerups";

// ─── Lobby playground (dev-only, /playground) ─────────────────────────────────
// Refined "design B": three-pane lobby, private by default. LEFT = two stacked
// cards (Lobby settings, then Players with add-individual-bots). Bot-fill is a
// 4-colour control (Off/Easy/Medium/Hard). Middle power-ups are always expanded.
// RIGHT = Invite + Chat as two boxes. Big OpenFront-style green Start button.

const C = { bg: "#070a12", panel: "#0e1422", panel2: "#111a2b", line: "rgba(255,255,255,0.08)", txt: "#eaf0ff", muted: "#8995b3", accent: "#28c4d4", pink: "#ff2e88", green: "#7CFC66" };
const UI = '"Rajdhani", sans-serif';
const LB = '"Orbitron", sans-serif';
// Difficulty colours: Off neutral, Easy green, Medium yellow, Hard red.
const LVL_COLOR: Record<string, string> = { Off: C.muted, Easy: "#5fd35f", Medium: "#f5c542", Hard: "#ff5b5b" };
function rgba(hex: string, a: number) { const n = parseInt(hex.replace("#", ""), 16); return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`; }

// Soft difficulty badge: tinted background + colored label + a small dot. Cleaner
// than a fully-saturated pill.
function LevelBadge({ level }: { level: string }) {
  const col = LVL_COLOR[level];
  return (
    <span style={{ display: "inline-flex", alignItems: "center", padding: "3px 12px", borderRadius: 20, background: rgba(col, 0.14), border: `1px solid ${rgba(col, 0.3)}`, color: col, fontFamily: LB, fontSize: 10, letterSpacing: 1.5, fontWeight: 700, textTransform: "uppercase" }}>
      {level}
    </span>
  );
}

const MAPS: MapOption[] = [
  { value: "sq500", name: "Small Square", shape: "square", size: 500 },
  { value: "sq800", name: "Medium Square", shape: "square", size: 800 },
  { value: "sq1000", name: "Large Square", shape: "square", size: 1000 },
  { value: "ci500", name: "Small Circle", shape: "circle", size: 500 },
  { value: "ci800", name: "Medium Circle", shape: "circle", size: 800 },
  { value: "ci1000", name: "Large Circle", shape: "circle", size: 1000 },
];
type Human = { name: string; color: string; host: boolean; local?: boolean; keys?: { left: string; right: string } };

// Local (same-computer) players each get their own turn keys. Player 1 (You) uses
// the arrows; extra couch players get the next scheme. A phone only fits 2 players
// (they split the screen left/right), so the cap is lower there.
const CONTROL_SCHEMES = [
  { left: "←", right: "→" },
  { left: "A", right: "D" },
  { left: "V", right: "N" },
  { left: "J", right: "L" },
];
const MAX_LOCAL_DESKTOP = CONTROL_SCHEMES.length;
const MAX_LOCAL_MOBILE = 2;
const PLAYER_COLORS = ["#ff2e88", "#28c4d4", "#7CFC66", "#f5c542", "#b06cff", "#ff9f45", "#ff5c5c", "#4f86ff"];

const INITIAL_HUMANS: Human[] = [
  { name: "You", color: C.pink, host: true, local: true, keys: CONTROL_SCHEMES[0] },
  { name: "TurboNoodle", color: C.accent, host: false },
  { name: "WigglyWill", color: C.green, host: false },
];
type ChatItem = { sys: "joined" | "left"; who: string; color: string } | { who: string; msg: string };
const CHAT: ChatItem[] = [
  { sys: "joined", who: "TurboNoodle", color: C.accent },
  { who: "TurboNoodle", msg: "gg last round" },
  { sys: "joined", who: "WigglyWill", color: C.green },
  { who: "You", msg: "adding a couple bots" },
  { who: "WigglyWill", msg: "ready when you are" },
  { sys: "left", who: "SneakyViper", color: "#b06cff" },
];

const Header = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <p style={{ fontFamily: LB, fontSize: 12, letterSpacing: 2, textTransform: "uppercase", color: C.muted, margin: "0 0 12px", ...style }}>{children}</p>
);
const Card = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <div style={{ background: C.panel, border: `1px solid ${C.line}`, borderRadius: 14, display: "flex", flexDirection: "column", minHeight: 0, ...style }}>{children}</div>
);
const Scroll = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <div style={{ overflowY: "auto", padding: 18, minHeight: 0, flex: 1, ...style }}>{children}</div>
);

const CopyIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);
const CopyBtn = ({ label = "Copy" }: { label?: string }) => (
  <button type="button" title={label} aria-label={label} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, borderRadius: 8, border: `1px solid ${C.line}`, background: "rgba(255,255,255,0.06)", color: C.muted, cursor: "pointer", flex: "0 0 auto" }}>
    <CopyIcon />
  </button>
);
const stepBtn: React.CSSProperties = { width: 30, height: 30, borderRadius: 8, border: `1px solid ${C.line}`, background: "rgba(255,255,255,0.05)", color: C.txt, fontSize: 18, cursor: "pointer", lineHeight: 1 };

// 4-colour Off/Easy/Medium/Hard control (each button carries its difficulty colour).
function LevelPicker({ options, value, onSelect, fullWidth }: { options: string[]; value: string; onSelect: (v: string) => void; fullWidth?: boolean }) {
  const container: React.CSSProperties = fullWidth
    ? { display: "grid", gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))`, gap: 6 }
    : { display: "inline-flex", gap: 6, flexWrap: "wrap" };
  return (
    <div style={container}>
      {options.map((o) => {
        const col = LVL_COLOR[o];
        const sel = o === value;
        return (
          <button key={o} type="button" onClick={() => onSelect(o)} style={{ boxSizing: "border-box", minWidth: 0, borderRadius: 8, padding: "13px 8px", fontWeight: 700, fontSize: 13, cursor: "pointer", border: `1.5px solid ${sel ? col : C.line}`, color: sel ? col : C.txt, background: sel ? rgba(col, 0.15) : "rgba(255,255,255,0.05)" }}>{o}</button>
        );
      })}
    </div>
  );
}

// ── LEFT card 1: lobby settings ──
function SettingsCard({ max, onDec, onInc, fill, setFill }: { max: number; onDec: () => void; onInc: () => void; fill: string; setFill: React.Dispatch<React.SetStateAction<string>> }) {
  return (
    <Card style={{ padding: "4px 16px 16px", flexShrink: 0 }}>
      <Header style={{ padding: "16px 0 0" }}>Player settings</Header>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "12px 0", borderTop: `1px solid ${C.line}` }}>
        <span style={{ fontWeight: 700 }}>Max players</span>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <button type="button" style={stepBtn} onClick={onDec}>−</button>
          <span style={{ fontFamily: LB, fontWeight: 700, minWidth: 24, textAlign: "center" }}>{max}</span>
          <button type="button" style={stepBtn} onClick={onInc}>+</button>
        </div>
      </div>
      <div style={{ padding: "12px 0 0", borderTop: `1px solid ${C.line}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <span style={{ fontWeight: 700 }}>Fill empty seats</span>
          <span style={{ color: C.muted, fontSize: 12 }}>with bots</span>
          <InfoDot title="Empty seats auto-fill with bots at this difficulty. To add specific bots or mix difficulties, use 'Add a bot' under the player list." />
        </div>
        <LevelPicker options={["Off", "Easy", "Medium", "Hard"]} value={fill} onSelect={setFill} fullWidth />
      </div>
    </Card>
  );
}

// ── LEFT card 2: players + add individual bots ──
let botSeq = 1;
function ControlChip({ keys, mobile, side }: { keys?: { left: string; right: string }; mobile?: boolean; side: number }) {
  if (mobile) {
    return <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: C.muted, fontFamily: UI, fontWeight: 700, fontSize: 12, whiteSpace: "nowrap" }}>{side === 0 ? "◧ Left half" : "◨ Right half"}</span>;
  }
  const key: React.CSSProperties = { display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 20, height: 20, padding: "0 5px", borderRadius: 5, background: "rgba(255,255,255,0.08)", border: `1px solid ${C.line}`, fontFamily: LB, fontWeight: 700, fontSize: 11, color: C.txt };
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 4, flex: "0 0 auto" }} title="Turn keys"><span style={key}>{keys?.left}</span><span style={key}>{keys?.right}</span></span>;
}

function PlayersCard({ max, fill, bots, setBots, humans, kickHuman, addLocal, maxLocal, mobile }: { max: number; fill: string; bots: { id: number; level: string }[]; setBots: React.Dispatch<React.SetStateAction<{ id: number; level: string }[]>>; humans: Human[]; kickHuman: (name: string) => void; addLocal: () => void; maxLocal: number; mobile?: boolean }) {
  const filled = humans.length + bots.length;
  const empties = Math.max(0, max - filled);
  const add = (level: string) => setBots((b) => (humans.length + b.length < max ? [...b, { id: botSeq++, level }] : b));
  const remove = (id: number) => setBots((b) => b.filter((x) => x.id !== id));
  const locals = humans.filter((h) => h.local);
  const localFull = locals.length >= maxLocal;
  const seatFull = humans.length >= max;
  const canAddLocal = !localFull && !seatFull;
  const nextScheme = CONTROL_SCHEMES[locals.length];
  return (
    <Card style={{ padding: 16, flexShrink: 0 }}>
      <Header>Players ({filled}/{max})</Header>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {humans.map((p) => {
          const side = p.local ? locals.findIndex((h) => h.name === p.name) : -1;
          return (
          <div key={p.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 11px", borderRadius: 10, background: "rgba(255,255,255,0.03)", border: `1px solid ${C.line}` }}>
            <span title={p.name === "You" ? "Change your color" : undefined} style={{ width: 14, height: 14, borderRadius: "50%", background: p.color, flex: "0 0 auto", cursor: p.name === "You" ? "pointer" : "default", boxShadow: p.name === "You" ? `0 0 0 3px ${rgba(p.color, 0.25)}` : "none" }} />
            <span style={{ fontWeight: 700, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}{p.host && <span style={{ color: C.muted, fontWeight: 400 }}> · host</span>}</span>
            {p.local && <ControlChip keys={p.keys} mobile={mobile} side={side} />}
            {!p.host && <button type="button" onClick={() => kickHuman(p.name)} title={p.local ? "Remove player" : "Kick player"} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, borderRadius: 6, background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 16, lineHeight: 1, flex: "0 0 auto" }}>×</button>}
          </div>
          );
        })}
        {bots.map((b, i) => (
          <div key={b.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 11px", borderRadius: 10, background: "rgba(255,255,255,0.03)", border: `1px solid ${C.line}` }}>
            <span style={{ width: 18, height: 18, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13, flex: "0 0 auto" }}>🤖</span>
            <span style={{ fontWeight: 700, flex: 1 }}>Bot {i + 1}</span>
            <LevelBadge level={b.level} />
            <button type="button" onClick={() => remove(b.id)} title="Remove" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, borderRadius: 6, background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>
          </div>
        ))}
        {Array.from({ length: empties }).map((_, i) => (
          <div key={"e" + i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 11px", borderRadius: 10, border: `1px dashed ${C.line}`, opacity: 0.6 }}>
            {fill !== "Off" ? (
              <>
                <span style={{ width: 18, height: 18, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13, flex: "0 0 auto" }}>🤖</span>
                <span style={{ flex: 1, color: C.muted }}>Bot <span style={{ fontSize: 12 }}>· auto-fill</span></span>
                <LevelBadge level={fill} />
                <span style={{ width: 22, flex: "0 0 auto" }} aria-hidden />
              </>
            ) : (
              <>
                <span style={{ width: 18, textAlign: "center", color: C.muted, flex: "0 0 auto" }}>+</span>
                <span style={{ flex: 1, color: C.muted }}>Open slot</span>
              </>
            )}
          </div>
        ))}
      </div>
      <div style={{ marginTop: 20 }}>
        <Header style={{ margin: "0 0 8px" }}>Local players</Header>
        <button type="button" onClick={addLocal} disabled={!canAddLocal} style={{ width: "100%", boxSizing: "border-box", padding: "12px", borderRadius: 10, cursor: canAddLocal ? "pointer" : "default", border: `1px dashed ${canAddLocal ? rgba(C.accent, 0.5) : C.line}`, background: canAddLocal ? rgba(C.accent, 0.06) : "transparent", color: canAddLocal ? C.txt : C.muted, fontWeight: 700, fontFamily: UI, fontSize: 13, opacity: canAddLocal ? 1 : 0.55, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>
          {localFull ? `Max ${maxLocal} on this ${mobile ? "phone" : "computer"}` : seatFull ? "Lobby is full" : <>Add player on this computer{!mobile && nextScheme && <span style={{ color: C.muted, fontWeight: 600 }}> &nbsp;{nextScheme.left} / {nextScheme.right}</span>}</>}
        </button>
        <p style={{ color: C.muted, fontSize: 12, margin: "8px 0 0", fontFamily: UI }}>{mobile ? "Up to 2 players share the phone, one per side of the screen." : "Everyone shares this keyboard, each with their own turn keys."}</p>
      </div>
      <div style={{ marginTop: 20 }}>
        <Header style={{ margin: "0 0 8px" }}>Add a bot</Header>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
          {["Easy", "Medium", "Hard"].map((lvl) => (
            <button key={lvl} type="button" onClick={() => add(lvl)} style={{ boxSizing: "border-box", minWidth: 0, padding: "14px 8px", borderRadius: 10, cursor: "pointer", border: `1px solid ${C.line}`, background: "rgba(255,255,255,0.05)", color: C.txt, fontWeight: 700, fontFamily: UI, fontSize: 13 }}>{lvl}</button>
          ))}
        </div>
      </div>
    </Card>
  );
}

function MapPicker() {
  const [sel, setSel] = useState("sq500");
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(128px, 1fr))", gap: 10 }}>
      {MAPS.map((m) => <StaticMapTile key={m.value} map={m} selected={sel === m.value} onClick={() => setSel(m.value)} />)}
    </div>
  );
}

// Clean 2-colour line icons (Lucide-style). Rendered white on a colour square for
// section headers, and muted for preset buttons — replaces the emoji icons.
function Icon({ name, size = 16, color = "currentColor" }: { name: string; size?: number; color?: string }) {
  const p = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  switch (name) {
    case "map": return (<svg {...p}><path d="M9 3 3 6v15l6-3 6 3 6-3V3l-6 3-6-3Z" /><path d="M9 3v15" /><path d="M15 6v15" /></svg>);
    case "info": return (<svg {...p}><circle cx="12" cy="12" r="10" /><line x1="12" y1="11" x2="12" y2="16" /><line x1="12" y1="8" x2="12" y2="8.01" /></svg>);
    case "trophy": return (<svg {...p}><path d="M6 9a6 6 0 0 0 12 0V4H6Z" /><path d="M6 4H4a2 2 0 0 0 0 4h2" /><path d="M18 4h2a2 2 0 0 1 0 4h-2" /><path d="M9 20h6" /><path d="M12 15v5" /></svg>);
    case "sliders": return (<svg {...p}><line x1="4" y1="8" x2="20" y2="8" /><line x1="4" y1="16" x2="20" y2="16" /><circle cx="9" cy="8" r="2.4" fill={color} stroke="none" /><circle cx="15" cy="16" r="2.4" fill={color} stroke="none" /></svg>);
    case "gauge": return (<svg {...p}><path d="M12 14l4-4" /><path d="M3.3 19a10 10 0 1 1 17.4 0" /></svg>);
    case "timer": return (<svg {...p}><circle cx="12" cy="13" r="8" /><path d="M12 13V9" /><path d="M12 13l3 2" /><path d="M9 2h6" /></svg>);
    case "bolt": return (<svg {...p}><path d="M13 2 3 14h9l-1 8 10-12h-9l1-8Z" /></svg>);
    case "target": return (<svg {...p}><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4.5" /><circle cx="12" cy="12" r="1" fill={color} stroke="none" /></svg>);
    case "question": return (<svg {...p}><circle cx="12" cy="12" r="10" /><path d="M9.2 9a3 3 0 0 1 5.6 1c0 2-3 2.5-3 4" /><line x1="12" y1="17.5" x2="12" y2="17.51" /></svg>);
    case "flame": return (<svg {...p}><g transform="translate(12 12) scale(1.15) translate(-12 -12) translate(0 1.6)"><path d="M12 2c3.5 4 6 6.5 6 10a6 6 0 0 1-12 0c0-2 1-3.8 2.6-5.2C9.5 9.5 11 6.5 12 2Z" /><path d="M12 18a3 3 0 0 0 3-3c0-1.9-1.2-3.2-3-4.8-1.8 1.6-3 2.9-3 4.8a3 3 0 0 0 3 3Z" fill={color} stroke="none" /></g></svg>);
    case "beer": return (<svg {...p}><path d="M6 8h9v11a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V8Z" /><path d="M15 11h2a2.5 2.5 0 0 1 0 5h-2" /><path d="M6 8c-.5-2 1-3.5 3-3 .3-1.7 2.7-1.7 3 0 2-.5 3.5 1 3 3" /><path d="M9.5 12v5M12 12v5" /></svg>);
    case "burger": return (<svg {...p}><path d="M4 11a8 5.5 0 0 1 16 0Z" /><path d="M4 14.5h16" /><path d="M5 18h14a1.5 1.5 0 0 0 0-3H5a1.5 1.5 0 0 0 0 3Z" /></svg>);
    case "users": return (<svg {...p}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>);
    case "cup": return (<svg {...p}><path d="M17 8h1a4 4 0 1 1 0 8h-1" /><path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V8Z" /><path d="M6 2v2" /><path d="M10 2v2" /><path d="M14 2v2" /></svg>);
    case "music": return (<svg {...p}><path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></svg>);
    case "clipboard": return (<svg {...p}><rect x="8" y="3" width="8" height="4" rx="1" /><path d="M16 5h2a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2" /></svg>);
    case "refresh": return (<svg {...p}><path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v5h-5" /></svg>);
    case "lock": return (<svg {...p}><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>);
    default: return null;
  }
}

// OpenFront/Catan-style section: icon square + uppercase title + divider line,
// full-width content.
function Section({ icon, color, title, children }: { icon: string; color: string; title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 36 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <span style={{ width: 30, height: 30, borderRadius: 8, background: rgba(color, 0.16), display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}><Icon name={icon} color={color} size={16} /></span>
        <span style={{ fontFamily: LB, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", fontSize: 14 }}>{title}</span>
        <span style={{ flex: 1, height: 1, background: C.line }} />
      </div>
      {children}
    </div>
  );
}

// Full-width equal segmented control (like OpenFront's difficulty/mode tiles).
function FullSeg({ options, value, onSelect }: { options: { label: string; value: number }[]; value: number; onSelect: (v: number) => void }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))`, gap: 8 }}>
      {options.map((o) => {
        const sel = o.value === value;
        return <button key={o.label} type="button" onClick={() => onSelect(o.value)} style={{ boxSizing: "border-box", minWidth: 0, padding: "17px 8px", borderRadius: 10, cursor: "pointer", border: `1.5px solid ${sel ? C.accent : C.line}`, background: sel ? rgba(C.accent, 0.12) : "rgba(255,255,255,0.03)", color: sel ? C.accent : C.txt, fontWeight: 700, fontFamily: UI, fontSize: 13 }}>{o.label}</button>;
      })}
    </div>
  );
}
// Score-to-win in a framed box with a slider + centered value (OpenFront options).
function ScoreBox({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div style={{ border: `1.5px solid ${rgba(C.accent, 0.3)}`, borderRadius: 12, padding: "16px 18px", background: rgba(C.accent, 0.04) }}>
      <input type="range" min={2} max={50} value={value} onChange={(e) => onChange(+e.target.value)} style={{ width: "100%", accentColor: C.accent }} />
      <div style={{ textAlign: "center", marginTop: 6 }}>
        <div style={{ fontFamily: LB, fontSize: 11, letterSpacing: 1.5, color: C.muted }}>SCORE TO WIN</div>
        <div style={{ fontFamily: LB, fontWeight: 900, fontSize: 22 }}>{value}</div>
      </div>
    </div>
  );
}

// Power-up toggle grid (full-width, real icons) — like OpenFront's disable-units grid.
function PowerToggles({ off, toggle }: { off: Set<string>; toggle: (k: string) => void }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8 }}>
      {POWERUPS.map((p) => {
        const on = !off.has(p.key);
        return (
          <button key={p.key} type="button" onClick={() => toggle(p.key)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 12px", borderRadius: 10, cursor: "pointer", border: `1px solid ${C.line}`, background: on ? "rgba(255,255,255,0.05)" : "transparent", color: C.txt, opacity: on ? 1 : 0.4, fontFamily: UI, fontWeight: 700, fontSize: 13, textAlign: "left" }}>
            <span style={{ width: 24, height: 24, borderRadius: "50%", background: POWERUP_BADGE[p.color], display: "inline-flex", flex: "0 0 auto" }}>
              <img src={`/svg/powerup/${p.icon}.svg`} alt="" style={{ display: "block", width: "100%", height: "100%" }} />
            </span>
            {p.name}
          </button>
        );
      })}
    </div>
  );
}

// Fun presets: each sets spawn rate, worm speed and which power-ups are ON.
const _byColor = (color: string) => new Set(POWERUPS.filter((p) => p.color !== color).map((p) => p.key));
const _onlyKey = (key: string) => new Set(POWERUPS.filter((p) => p.key !== key).map((p) => p.key));
const _onlyKeys = (keys: string[]) => new Set(POWERUPS.filter((p) => !keys.includes(p.key)).map((p) => p.key));
const PRESETS: { name: string; icon: string; spawn: number; speed: number; off: Set<string> }[] = [
  { name: "Classic", icon: "target", spawn: SPAWN_RATES[2].value, speed: WORM_SPEEDS[1].value, off: new Set() },
  { name: "Identity crisis", icon: "users", spawn: SPAWN_RATES[3].value, speed: WORM_SPEEDS[1].value, off: _onlyKeys(["switcharoonie", "twin"]) },
  { name: "Sabotage", icon: "flame", spawn: SPAWN_RATES[3].value, speed: WORM_SPEEDS[1].value, off: _byColor("red") },
  { name: "Drink and drive", icon: "beer", spawn: SPAWN_RATES[4].value, speed: WORM_SPEEDS[1].value, off: _onlyKey("drunk") },
  { name: "Fatboy Slim", icon: "burger", spawn: SPAWN_RATES[3].value, speed: WORM_SPEEDS[1].value, off: _onlyKeys(["fat", "slim"]) },
];

function MiddleConfig() {
  const [winMode, setWinMode] = useState<"score" | "rounds">("score");
  const [score, setScore] = useState(10);
  const [rounds, setRounds] = useState(10);
  const [speed, setSpeed] = useState(WORM_SPEEDS[1].value);
  const [spawn, setSpawn] = useState(SPAWN_RATES[2].value);
  const [off, setOff] = useState<Set<string>>(new Set());
  const [selectedPreset, setSelectedPreset] = useState("Classic");
  const toggle = (k: string) => setOff((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const apply = (p: (typeof PRESETS)[number]) => { setSelectedPreset(p.name); setSpawn(p.spawn); setSpeed(p.speed); setOff(new Set(p.off)); };
  return (
    <>
      <Section icon="map" color={C.accent} title="Map"><MapPicker /></Section>
      <Section icon="trophy" color="#f5c542" title="Winning condition">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <WinBox label="Score to win" active={winMode === "score"} onSelect={() => setWinMode("score")} value={score} min={2} max={50} onChange={setScore} />
          <WinBox label="Rounds to play" active={winMode === "rounds"} onSelect={() => setWinMode("rounds")} value={rounds} min={1} max={20} onChange={setRounds} />
        </div>
        <p style={{ color: C.muted, fontSize: 12, margin: "10px 0 0" }}>On a tie, extra rounds are played until there's a clear winner.</p>
      </Section>
      <Section icon="sliders" color={C.pink} title="Presets">
        <p style={{ color: C.muted, fontSize: 13, margin: "-4px 0 12px" }}>Quick setups that change all the settings below.</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
          {PRESETS.map((p) => {
            const sel = selectedPreset === p.name;
            return (
              <button key={p.name} type="button" onClick={() => apply(p)} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "15px 14px", borderRadius: 10, cursor: "pointer", border: `1.5px solid ${sel ? C.accent : C.line}`, background: sel ? rgba(C.accent, 0.12) : "rgba(255,255,255,0.05)", color: sel ? C.accent : C.txt, fontWeight: 700, fontFamily: UI, fontSize: 13 }}>
                <Icon name={p.icon} size={16} color={sel ? C.accent : C.muted} />{p.name}
              </button>
            );
          })}
        </div>
      </Section>
      <Section icon="gauge" color={C.green} title="Worm speed"><FullSeg options={WORM_SPEEDS} value={speed} onSelect={setSpeed} /></Section>
      <Section icon="timer" color="#ff9f45" title="Spawn rate"><FullSeg options={SPAWN_RATES} value={spawn} onSelect={setSpawn} /></Section>
      {spawn > 0 && <Section icon="bolt" color="#b06cff" title="Power-ups"><PowerToggles off={off} toggle={toggle} /></Section>}
    </>
  );
}

function FakeQR({ size = 96 }: { size?: number }) {
  const n = 9, cells = [];
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
    const corner = (x < 3 && y < 3) || (x > n - 4 && y < 3) || (x < 3 && y > n - 4);
    if (corner || (x * 7 + y * 13 + x * y) % 3 === 0) cells.push(<rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" fill="#06121a" />);
  }
  return <div style={{ background: "#fff", borderRadius: 10, padding: 8, flex: "0 0 auto" }}><svg width={size} height={size} viewBox={`0 0 ${n} ${n}`} shapeRendering="crispEdges">{cells}</svg></div>;
}

function InviteCard() {
  return (
    <Card style={{ padding: 16, flex: "0 0 auto" }}>
      <Header>Invite</Header>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "center" }}>
        <FakeQR />
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontFamily: LB, letterSpacing: 3, fontWeight: 700, fontSize: 20 }}>ACHTG-7Q</span>
          <CopyBtn label="Copy code" />
        </div>
        <div style={{ display: "flex", gap: 8, width: "100%", alignItems: "center" }}>
          <div style={{ flex: 1, padding: "9px 10px", borderRadius: 8, background: "rgba(255,255,255,0.04)", border: `1px solid ${C.line}`, fontSize: 12, wordBreak: "break-all", color: C.muted }}>achtungroyale.io/j/ACHTG-7Q</div>
          <CopyBtn label="Copy link" />
        </div>
      </div>
    </Card>
  );
}

function ChatCard() {
  return (
    <Card style={{ padding: 16, flex: 1 }}>
      <Header>Chat</Header>
      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, minHeight: 0 }}>
        {CHAT.map((c, i) =>
          "sys" in c ? (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, margin: "2px 0", color: C.muted, fontSize: 12 }}>
              <span style={{ height: 1, background: C.line, flex: 1 }} />
              <span style={{ whiteSpace: "nowrap" }}>
                <span style={{ color: c.color, fontWeight: 700 }}>{c.who}</span> {c.sys === "joined" ? "joined" : "left"}
              </span>
              <span style={{ height: 1, background: C.line, flex: 1 }} />
            </div>
          ) : (
            <div key={i} style={{ fontSize: 14 }}><span style={{ fontWeight: 700, color: c.who === "You" ? C.pink : C.accent }}>{c.who}: </span><span>{c.msg}</span></div>
          )
        )}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 10, flex: "0 0 auto" }}>
        <input placeholder="Send a message" style={{ flex: 1, background: "rgba(255,255,255,0.05)", border: `1px solid ${C.line}`, borderRadius: 8, padding: "8px 10px", color: C.txt, fontFamily: UI }} />
        <button type="button" style={{ borderRadius: 8, border: "none", padding: "0 14px", background: C.accent, color: "#06121a", cursor: "pointer" }}>➤</button>
      </div>
    </Card>
  );
}

function LobbyTitle() {
  const [name, setName] = useState("OMMatte's Lobby");
  const [pub, setPub] = useState(false);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", borderBottom: `1px solid ${C.line}`, flex: "0 0 auto", flexWrap: "wrap" }}>
      <button type="button" title="Leave lobby" style={backBtn}>←</button>
      <input value={name} onChange={(e) => setName(e.target.value)} title="Click to rename the lobby" style={{ fontFamily: LB, fontWeight: 900, fontSize: 20, background: "transparent", border: "none", color: C.txt, minWidth: 0, flex: 1 }} />
      <span style={{ fontSize: 12, fontFamily: LB, letterSpacing: 1, color: pub ? C.green : C.muted, textTransform: "uppercase" }}>{pub ? "🌐 Public" : "🔒 Private"}</span>
      <button type="button" onClick={() => setPub((p) => !p)} style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.line}`, background: "rgba(255,255,255,0.06)", color: C.txt, fontWeight: 700, cursor: "pointer", fontFamily: UI, fontSize: 13 }}>{pub ? "Make private" : "Make public"}</button>
    </div>
  );
}

const tabStyle = (a: boolean): React.CSSProperties => ({ padding: "8px 14px", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: LB, fontWeight: 700, fontSize: 12, letterSpacing: 1, color: a ? "#06121a" : C.txt, background: a ? C.accent : "rgba(255,255,255,0.06)" });
const backBtn: React.CSSProperties = { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: 10, border: `1px solid ${C.line}`, background: "rgba(255,255,255,0.06)", color: C.txt, cursor: "pointer", fontSize: 18, flex: "0 0 auto" };

// Mock site nav header (to judge the vertical space it eats). Includes a Create/Join
// view toggle for the playground.
type LobbyView = "local" | "create" | "join";
function MockNav({ view, setView, device, setDevice }: { view: LobbyView; setView: (v: LobbyView) => void; device: "desktop" | "mobile"; setDevice: (d: "desktop" | "mobile") => void }) {
  const link: React.CSSProperties = { color: C.muted, fontFamily: LB, fontWeight: 700, fontSize: 12, letterSpacing: 1, textTransform: "uppercase", cursor: "pointer" };
  return (
    <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 18, padding: "12px 22px", borderBottom: `1px solid ${C.line}`, flexWrap: "wrap" }}>
      <span style={{ fontFamily: '"Bungee", sans-serif', fontSize: 17, color: C.accent }}>ACHTUNG ROYALE</span>
      <span style={{ ...link, color: C.txt }}>Play</span>
      <span style={link}>News</span>
      <span style={link}>Leaderboard</span>
      <span style={link}>Help</span>
      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
        {/* playground-only: preview device + which screen */}
        <button type="button" onClick={() => setDevice("desktop")} style={tabStyle(device === "desktop")}>🖥</button>
        <button type="button" onClick={() => setDevice("mobile")} style={tabStyle(device === "mobile")}>📱</button>
        <span style={{ width: 1, height: 20, background: C.line, margin: "0 6px" }} />
        <button type="button" onClick={() => setView("local")} style={tabStyle(view === "local")}>Play locally</button>
        <button type="button" onClick={() => setView("create")} style={tabStyle(view === "create")}>Create lobby</button>
        <button type="button" onClick={() => setView("join")} style={tabStyle(view === "join")}>Join lobby</button>
        <span style={{ ...link, marginLeft: 10, padding: "8px 16px", border: `1px solid ${C.line}`, borderRadius: 20, color: C.txt }}>Sign in</span>
      </div>
    </div>
  );
}

function InfoDot({ title }: { title: string }) {
  return <span title={title} style={{ display: "inline-flex", alignItems: "center", color: C.muted, cursor: "help", flex: "0 0 auto" }}><Icon name="info" size={15} color={C.muted} /></span>;
}

// Compact slider box (label + value on one row, slider below). Two fit side by side.
function SliderBox({ label, value, min, max, suffix = "", onChange }: { label: string; value: number; min: number; max: number; suffix?: string; onChange: (v: number) => void }) {
  return (
    <div style={{ flex: 1, minWidth: 0, border: `1px solid ${C.line}`, borderRadius: 10, padding: "12px 14px", background: "rgba(255,255,255,0.02)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8, gap: 6 }}>
        <span style={{ fontFamily: LB, fontSize: 11, letterSpacing: 1, color: C.muted, textTransform: "uppercase" }}>{label}</span>
        <span style={{ fontFamily: LB, fontWeight: 900, fontSize: 18, flex: "0 0 auto" }}>{value}{suffix}</span>
      </div>
      <input type="range" min={min} max={max} value={value} onChange={(e) => onChange(+e.target.value)} style={{ width: "100%", accentColor: C.accent }} />
    </div>
  );
}

// Selectable winning-condition box: pick this one (radio) and its slider becomes
// active; the other greys out.
function WinBox({ label, active, onSelect, value, min, max, suffix = "", onChange }: { label: string; active: boolean; onSelect: () => void; value: number; min: number; max: number; suffix?: string; onChange: (v: number) => void }) {
  return (
    <div onClick={onSelect} style={{ flex: "1 1 210px", minWidth: 0, cursor: "pointer", border: `1.5px solid ${active ? C.accent : C.line}`, borderRadius: 10, padding: "12px 14px", background: active ? rgba(C.accent, 0.08) : "rgba(255,255,255,0.02)", opacity: active ? 1 : 0.55 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span style={{ width: 15, height: 15, borderRadius: "50%", border: `2px solid ${active ? C.accent : C.muted}`, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}>{active && <span style={{ width: 7, height: 7, borderRadius: "50%", background: C.accent }} />}</span>
        <span style={{ fontFamily: LB, fontSize: 11, letterSpacing: 1, color: active ? C.accent : C.muted, textTransform: "uppercase" }}>{label}</span>
        <span style={{ marginLeft: "auto", fontFamily: LB, fontWeight: 900, fontSize: 18 }}>{value}{suffix}</span>
      </div>
      <input type="range" min={min} max={max} value={value} disabled={!active} onChange={(e) => onChange(+e.target.value)} onClick={(e) => e.stopPropagation()} style={{ width: "100%", accentColor: active ? C.accent : C.muted }} />
    </div>
  );
}

// Placeholder public lobbies (real data comes from the edge-server later). Each
// row is one open public match a player can jump straight into.
const PUBLIC_LOBBIES = [
  { id: 1, name: "TurboNeedle's room", map: "Medium Square", mode: "Classic", players: 3, max: 6 },
  { id: 2, name: "chaos only pls", map: "Large Circle", mode: "Sabotage", players: 5, max: 8 },
  { id: 3, name: "drunk drivers", map: "Small Square", mode: "Drink and drive", players: 2, max: 4 },
  { id: 4, name: "sweat lodge", map: "Large Square", mode: "Classic", players: 6, max: 6 },
  { id: 5, name: "casual vibes only", map: "Medium Circle", mode: "Fatboy Slim", players: 1, max: 10 },
  { id: 6, name: "eu west chill", map: "Small Circle", mode: "Who am I?", players: 4, max: 8 },
];

const PUB_COLS = "minmax(0,1fr) 130px 128px 78px 86px"; // Lobby / Map / Mode / Players / Join

function PublicRow({ lobby }: { lobby: (typeof PUBLIC_LOBBIES)[number] }) {
  const [hover, setHover] = useState(false);
  const full = lobby.players >= lobby.max;
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ display: "grid", gridTemplateColumns: PUB_COLS, alignItems: "center", gap: 8, padding: "11px 14px", borderTop: `1px solid ${C.line}`, background: hover && !full ? "rgba(40,196,212,0.06)" : "transparent" }}
    >
      <span style={{ fontFamily: UI, fontWeight: 700, fontSize: 15, color: C.txt, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lobby.name}</span>
      <span style={{ fontFamily: UI, fontSize: 14, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lobby.map}</span>
      <span style={{ fontFamily: UI, fontSize: 14, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lobby.mode}</span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: LB, fontWeight: 700, fontSize: 13, color: full ? C.muted : C.txt }}>
        <Icon name="users" size={13} color={full ? C.muted : C.accent} />{lobby.players}/{lobby.max}
      </span>
      <button type="button" disabled={full} style={{ padding: "8px 0", borderRadius: 8, border: "none", cursor: full ? "default" : "pointer", fontFamily: LB, fontWeight: 900, letterSpacing: 1, fontSize: 12, color: full ? C.muted : "#06121a", background: full ? "rgba(255,255,255,0.06)" : C.green }}>{full ? "FULL" : "JOIN"}</button>
    </div>
  );
}

function JoinScreen({ onBack }: { onBack: () => void }) {
  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "32px 16px", overflowY: "auto" }}>
      <div style={{ width: "100%", maxWidth: 720, display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button type="button" onClick={onBack} title="Back" style={backBtn}>←</button>
          <h2 style={{ fontFamily: LB, fontWeight: 900, margin: 0, fontSize: 22 }}>Join a lobby</h2>
        </div>

        {/* Paste-in lobby ID (OpenFront-style) */}
        <Card style={{ padding: 20 }}>
          <p style={{ color: C.muted, margin: "0 0 12px", fontSize: 13, fontFamily: UI, fontWeight: 600 }}>Got a code from a friend? Paste the lobby ID to join a private game.</p>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
              <input placeholder="Enter lobby ID" style={{ width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.05)", border: `1px solid ${C.line}`, borderRadius: 10, padding: "15px 46px 15px 16px", color: C.txt, fontFamily: LB, fontSize: 16, letterSpacing: 1 }} />
              <button type="button" title="Paste from clipboard" style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "none", background: "transparent", color: C.muted, cursor: "pointer" }}><Icon name="clipboard" size={18} color={C.muted} /></button>
            </div>
            <button type="button" style={{ flex: "0 0 auto", padding: "0 28px", borderRadius: 10, border: "none", cursor: "pointer", fontFamily: LB, fontWeight: 900, letterSpacing: 2, fontSize: 15, color: "#06121a", background: C.green }}>JOIN LOBBY</button>
          </div>
        </Card>

        {/* Public lobbies list (Colonist-style) */}
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px" }}>
            <h3 style={{ fontFamily: LB, fontWeight: 900, margin: 0, fontSize: 16 }}>Public lobbies</h3>
            <span style={{ fontFamily: UI, fontWeight: 700, fontSize: 13, color: C.muted, background: "rgba(255,255,255,0.06)", borderRadius: 20, padding: "2px 10px" }}>{PUBLIC_LOBBIES.length}</span>
            <button type="button" title="Refresh" style={{ marginLeft: "auto", width: 34, height: 34, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: `1px solid ${C.line}`, background: "rgba(255,255,255,0.05)", color: C.muted, cursor: "pointer" }}><Icon name="refresh" size={16} color={C.muted} /></button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: PUB_COLS, alignItems: "center", gap: 8, padding: "8px 14px", background: "rgba(255,255,255,0.02)", borderTop: `1px solid ${C.line}`, fontFamily: LB, fontWeight: 700, fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase", color: C.muted }}>
            <span>Lobby</span><span>Map</span><span>Mode</span><span>Players</span><span />
          </div>
          {PUBLIC_LOBBIES.map((l) => <PublicRow key={l.id} lobby={l} />)}
        </Card>
      </div>
    </div>
  );
}

// Mobile layout: single phone-width column with Players / Settings / Chat tabs and
// a pinned Start button. Reuses the same cards + state as the desktop layout.
function MobileLobby({ max, onDec, onInc, fill, setFill, bots, setBots, humans, kickHuman, addLocal, local }: { max: number; onDec: () => void; onInc: () => void; fill: string; setFill: React.Dispatch<React.SetStateAction<string>>; bots: { id: number; level: string }[]; setBots: React.Dispatch<React.SetStateAction<{ id: number; level: string }[]>>; humans: Human[]; kickHuman: (name: string) => void; addLocal: () => void; local?: boolean }) {
  const [tab, setTab] = useState<"players" | "settings" | "chat">("settings");
  const [pub, setPub] = useState(false);
  const tabBtn = (a: boolean): React.CSSProperties => ({ flex: 1, padding: "13px 4px", border: "none", cursor: "pointer", fontFamily: LB, fontWeight: 700, fontSize: 12, letterSpacing: 1, textTransform: "uppercase", color: a ? C.accent : C.muted, background: "transparent", borderBottom: `2px solid ${a ? C.accent : "transparent"}` });
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0, background: C.panel, border: `1px solid ${C.line}`, borderRadius: 16, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderBottom: `1px solid ${C.line}`, flex: "0 0 auto" }}>
        <button type="button" title="Leave" style={backBtn}>←</button>
        <span style={{ fontFamily: LB, fontWeight: 900, fontSize: 16, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{local ? "New local match" : "OMMatte's Lobby"}</span>
        {!local && <button type="button" onClick={() => setPub((p) => !p)} title={pub ? "Make private" : "Make public"} style={{ fontSize: 11, fontFamily: LB, letterSpacing: 1, color: pub ? C.green : C.muted, background: "rgba(255,255,255,0.06)", border: `1px solid ${C.line}`, borderRadius: 8, padding: "7px 10px", cursor: "pointer", flex: "0 0 auto" }}>{pub ? "🌐 PUBLIC" : "🔒 PRIVATE"}</button>}
      </div>
      <div style={{ display: "flex", borderBottom: `1px solid ${C.line}`, flex: "0 0 auto" }}>
        <button type="button" onClick={() => setTab("players")} style={tabBtn(tab === "players")}>Players</button>
        <button type="button" onClick={() => setTab("settings")} style={tabBtn(tab === "settings")}>Settings</button>
        {!local && <button type="button" onClick={() => setTab("chat")} style={tabBtn(tab === "chat")}>Room</button>}
      </div>
      {tab === "chat" && !local ? (
        <div style={{ flex: 1, minHeight: 0, padding: 12, display: "flex", flexDirection: "column", gap: 12 }}>
          <InviteCard />
          <ChatCard />
        </div>
      ) : (
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 12 }}>
          {tab === "players" && (<><SettingsCard max={max} onDec={onDec} onInc={onInc} fill={fill} setFill={setFill} /><PlayersCard max={max} fill={fill} bots={bots} setBots={setBots} humans={humans} kickHuman={kickHuman} addLocal={addLocal} maxLocal={MAX_LOCAL_MOBILE} mobile /></>)}
          {tab === "settings" && <MiddleConfig />}
        </div>
      )}
      <div style={{ flex: "0 0 auto", borderTop: `1px solid ${C.line}`, background: C.panel2, padding: 12 }}>
        <button type="button" style={{ width: "100%", padding: "16px", borderRadius: 10, border: "none", cursor: "pointer", fontFamily: LB, fontWeight: 900, letterSpacing: 2, fontSize: 16, color: "#06121a", background: C.green }}>START GAME</button>
      </div>
    </div>
  );
}

// Single-player (Play Locally) desktop: two columns — players + settings on the
// left, the game config + Start on the right. No invite/chat (nobody to invite).
function SinglePlayerDesktop({ max, onDec, onInc, fill, setFill, bots, setBots, humans, kickHuman, addLocal }: { max: number; onDec: () => void; onInc: () => void; fill: string; setFill: React.Dispatch<React.SetStateAction<string>>; bots: { id: number; level: string }[]; setBots: React.Dispatch<React.SetStateAction<{ id: number; level: string }[]>>; humans: Human[]; kickHuman: (name: string) => void; addLocal: () => void }) {
  return (
    <div style={{ flex: 1, minHeight: 0, padding: 12, display: "flex", gap: 12, justifyContent: "center" }}>
      <div style={{ flex: "1 1 300px", minWidth: 280, maxWidth: 380, display: "flex", flexDirection: "column", gap: 12, minHeight: 0, overflowY: "auto" }}>
        <SettingsCard max={max} onDec={onDec} onInc={onInc} fill={fill} setFill={setFill} />
        <PlayersCard max={max} fill={fill} bots={bots} setBots={setBots} humans={humans} kickHuman={kickHuman} addLocal={addLocal} maxLocal={MAX_LOCAL_DESKTOP} />
      </div>
      <Card style={{ flex: "2 1 620px", minWidth: 340, maxWidth: 780 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", borderBottom: `1px solid ${C.line}`, flex: "0 0 auto" }}>
          <button type="button" title="Back" style={backBtn}>←</button>
          <h2 style={{ fontFamily: LB, fontWeight: 900, margin: 0, fontSize: 20 }}>New local match</h2>
        </div>
        <Scroll><MiddleConfig /></Scroll>
        <div style={{ flex: "0 0 auto", borderTop: `1px solid ${C.line}`, background: C.panel2, padding: 16, borderRadius: "0 0 14px 14px" }}>
          <button type="button" style={{ width: "100%", padding: "20px", borderRadius: 12, border: "none", cursor: "pointer", fontFamily: LB, fontWeight: 900, letterSpacing: 3, fontSize: 20, color: "#06121a", background: C.green, boxShadow: `0 6px 24px rgba(124,252,102,0.25)` }}>START GAME</button>
        </div>
      </Card>
    </div>
  );
}

export default function PlaygroundPage() {
  const [view, setView] = useState<LobbyView>("create");
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");
  const [humans, setHumans] = useState(INITIAL_HUMANS);
  const [max, setMax] = useState(9);
  const [fill, setFill] = useState("Medium");
  const [bots, setBots] = useState<{ id: number; level: string }[]>([{ id: 0, level: "Hard" }]);
  const [pendingKick, setPendingKick] = useState(false);

  // Bots get removed automatically when max shrinks below humans + bots.
  useEffect(() => {
    const room = Math.max(0, max - humans.length);
    setBots((b) => (b.length > room ? b.slice(0, room) : b));
  }, [max, humans.length]);

  const decMax = () => {
    if (max <= 2) return;
    if (max - 1 < humans.length) { setPendingKick(true); return; } // would kick a human → warn
    setMax((m) => m - 1);
  };
  const incMax = () => setMax((m) => Math.min(16, m + 1));
  const confirmKick = () => {
    setHumans((h) => h.slice(0, -1)); // kick the last-joined player
    setMax((m) => Math.max(2, m - 1));
    setPendingKick(false);
  };
  const lastJoined = humans[humans.length - 1];
  const kickHuman = (name: string) => setHumans((h) => h.filter((p) => p.name !== name));

  // Add another player on this same device (couch co-op). Each gets the next free
  // control scheme + colour; a phone only fits 2, and humans bump auto-fill bots.
  const maxLocal = device === "mobile" ? MAX_LOCAL_MOBILE : MAX_LOCAL_DESKTOP;
  const addLocal = () =>
    setHumans((h) => {
      const locals = h.filter((x) => x.local);
      if (locals.length >= maxLocal || h.length >= max) return h;
      const used = new Set(h.map((x) => x.color));
      const color = PLAYER_COLORS.find((c) => !used.has(c)) || PLAYER_COLORS[locals.length % PLAYER_COLORS.length];
      const keys = CONTROL_SCHEMES[locals.length] || CONTROL_SCHEMES[CONTROL_SCHEMES.length - 1];
      return [...h, { name: `Player ${locals.length + 1}`, color, host: false, local: true, keys }];
    });
  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: C.bg, color: C.txt, fontFamily: UI, overflow: "hidden" }}>
      <MockNav view={view} setView={setView} device={device} setDevice={setDevice} />
      {view === "join" ? <JoinScreen onBack={() => setView("create")} /> : device === "mobile" ? (
        <div style={{ flex: 1, minHeight: 0, display: "flex", justifyContent: "center", padding: 12 }}>
          <div style={{ width: 400, maxWidth: "100%", height: "100%" }}>
            <MobileLobby max={max} onDec={decMax} onInc={incMax} fill={fill} setFill={setFill} bots={bots} setBots={setBots} humans={humans} kickHuman={kickHuman} addLocal={addLocal} local={view === "local"} />
          </div>
        </div>
      ) : view === "local" ? (
        <SinglePlayerDesktop max={max} onDec={decMax} onInc={incMax} fill={fill} setFill={setFill} bots={bots} setBots={setBots} humans={humans} kickHuman={kickHuman} addLocal={addLocal} />
      ) : (
      <div style={{ flex: 1, minHeight: 0, padding: 12, display: "flex", gap: 12 }}>
        {/* left: two stacked cards */}
        <div style={{ flex: "1 1 280px", minWidth: 260, display: "flex", flexDirection: "column", gap: 12, minHeight: 0, overflowY: "auto" }}>
          <SettingsCard max={max} onDec={decMax} onInc={incMax} fill={fill} setFill={setFill} />
          <PlayersCard max={max} fill={fill} bots={bots} setBots={setBots} humans={humans} kickHuman={kickHuman} addLocal={addLocal} maxLocal={MAX_LOCAL_DESKTOP} />
        </div>
        {/* center: title + config + big Start footer */}
        <Card style={{ flex: "3 1 520px", minWidth: 340 }}>
          <LobbyTitle />
          <Scroll>
            <MiddleConfig />
          </Scroll>
          <div style={{ flex: "0 0 auto", borderTop: `1px solid ${C.line}`, background: C.panel2, padding: 16, borderRadius: "0 0 14px 14px" }}>
            <button type="button" style={{ width: "100%", padding: "20px", borderRadius: 12, border: "none", cursor: "pointer", fontFamily: LB, fontWeight: 900, letterSpacing: 3, fontSize: 20, color: "#06121a", background: C.green, boxShadow: `0 6px 24px rgba(124,252,102,0.25)` }}>START GAME</button>
          </div>
        </Card>
        {/* right: invite + chat boxes */}
        <div style={{ flex: "1 1 280px", minWidth: 240, display: "flex", flexDirection: "column", gap: 12, minHeight: 0 }}>
          <InviteCard />
          <ChatCard />
        </div>
      </div>
      )}
      {pendingKick && (
        <div onClick={() => setPendingKick(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: C.panel, border: `1px solid ${C.line}`, borderRadius: 16, padding: 24, maxWidth: 360, textAlign: "center" }}>
            <div style={{ fontSize: 30, marginBottom: 6 }}>⚠️</div>
            <h3 style={{ fontFamily: LB, margin: "0 0 8px", fontSize: 18 }}>Reduce max players?</h3>
            <p style={{ color: C.muted, margin: "0 0 20px", lineHeight: 1.4 }}>This will kick the last-joined player, <b style={{ color: C.txt }}>{lastJoined?.name}</b>. Continue?</p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button type="button" onClick={() => setPendingKick(false)} style={{ padding: "10px 18px", borderRadius: 10, border: `1px solid ${C.line}`, background: "rgba(255,255,255,0.06)", color: C.txt, fontWeight: 700, cursor: "pointer", fontFamily: UI }}>Cancel</button>
              <button type="button" onClick={confirmKick} style={{ padding: "10px 18px", borderRadius: 10, border: "none", background: "#ff5b5b", color: "#06121a", fontWeight: 800, cursor: "pointer", fontFamily: UI }}>Kick &amp; continue</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
